import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { SmsNotificationNodeData } from '../../../converters/shared/reactFlowTypes'

/**
 * Hook for SMS notification node functionality.
 * Handles message type, message, phone number, send from, and auto-reply updates.
 */
export const useSmsNotificationNode = (
  id: string,
  data: SmsNotificationNodeData & Record<string, unknown>,
) => {
  const { updateNodeData } = useReactFlow()
  const dataRef = useRef(data)
  const isUpdatingRef = useRef(false)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // Local state for message and phoneNumber inputs (to prevent focus loss)
  const [message, setMessage] = useState(
    (data.message as string | undefined) || '',
  )
  const [phoneNumber, setPhoneNumber] = useState(
    (data.phoneNumber as string | undefined) || '',
  )
  const [messageError, setMessageError] = useState<string | null>(null)
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null)

  // Read save-time validation errors from node data
  useEffect(() => {
    const validationErrors = data.validationErrors as
      Record<string, string> | undefined
    if (validationErrors) {
      if (validationErrors.message) {
        setMessageError(validationErrors.message)
      }
      if (validationErrors.phoneNumber) {
        setPhoneNumberError(validationErrors.phoneNumber)
      }
    } else {
      // Clear errors if validationErrors is cleared
      setMessageError(null)
      setPhoneNumberError(null)
    }
  }, [data.validationErrors])

  // Sync local state from external data (only when not updating)
  useEffect(() => {
    if (!isUpdatingRef.current) {
      setMessage((data.message as string | undefined) || '')
      setPhoneNumber((data.phoneNumber as string | undefined) || '')
    }
  }, [data.message, data.phoneNumber])

  const handleMessageTypeChange = useCallback(
    (messageType: string | undefined, nextMessage?: string) => {
      const patch: Record<string, unknown> = {
        messageType,
      }

      if (nextMessage !== undefined) {
        setMessage(nextMessage)
        setMessageError(null)
        patch.message = nextMessage || undefined

        if (dataRef.current.validationErrors?.message) {
          const updatedErrors = { ...dataRef.current.validationErrors }
          delete updatedErrors.message
          patch.validationErrors =
            Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined
        }
      }

      updateNodeData(id, patch)
    },
    [id, updateNodeData],
  )

  const handleMessageChange = useCallback(
    (value: string) => {
      isUpdatingRef.current = true
      setMessage(value)
      // Clear validation error when user starts typing
      if (messageError && dataRef.current.validationErrors?.message) {
        const updatedErrors = { ...dataRef.current.validationErrors }
        delete updatedErrors.message
        updateNodeData(id, {
          ...dataRef.current,
          validationErrors:
            Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
        })
        setMessageError(null)
      }
    },
    [id, updateNodeData, messageError],
  )

  const handleMessageBlur = useCallback(() => {
    // Validate message format only (length > 1000), not empty validation
    // Empty validation is handled at save time only
    const trimmedMessage = message.trim()
    let error: string | null = null

    // Only show format errors, not empty field errors
    if (trimmedMessage.length > 1000) {
      error = 'Message must be 1000 characters or less'
    }

    // Clear save-time validation error when user makes changes
    if (trimmedMessage && dataRef.current.validationErrors?.message) {
      const updatedErrors = { ...dataRef.current.validationErrors }
      delete updatedErrors.message
      updateNodeData(id, {
        ...dataRef.current,
        message: trimmedMessage || undefined,
        validationErrors:
          Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
      })
      setMessageError(error)
      isUpdatingRef.current = false
      return
    }

    setMessageError(error)

    updateNodeData(id, {
      ...dataRef.current,
      message: trimmedMessage || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, message])

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      isUpdatingRef.current = true
      setPhoneNumber(value)
      // Clear validation error when user starts typing
      if (phoneNumberError && dataRef.current.validationErrors?.phoneNumber) {
        const updatedErrors = { ...dataRef.current.validationErrors }
        delete updatedErrors.phoneNumber
        updateNodeData(id, {
          ...dataRef.current,
          validationErrors:
            Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
        })
        setPhoneNumberError(null)
      }
    },
    [id, updateNodeData, phoneNumberError],
  )

  const handlePhoneNumberBlur = useCallback(() => {
    // Validate phone number format only (3-20 digits per number), not empty validation
    // Empty validation is handled at save time only
    const autoReply = dataRef.current.autoReplyToCaller
    const isAutoReplyOff = autoReply === false

    let error: string | null = null

    if (isAutoReplyOff) {
      const trimmedPhoneNumber = phoneNumber.trim()
      // Only validate format if value exists - don't show error for empty fields
      if (trimmedPhoneNumber) {
        // Strip all non-numeric characters except commas
        const numbers = trimmedPhoneNumber.split(',').map((n) => n.trim())
        const invalidNumbers = numbers.filter((num) => {
          const digits = num.replace(/\D/g, '')
          return digits.length < 3 || digits.length > 20
        })

        if (invalidNumbers.length > 0) {
          error = 'Each phone number must be 3-20 digits'
        }
      }
    }

    // Clear save-time validation error when user makes changes
    if (phoneNumber && dataRef.current.validationErrors?.phoneNumber) {
      const updatedErrors = { ...dataRef.current.validationErrors }
      delete updatedErrors.phoneNumber
      updateNodeData(id, {
        ...dataRef.current,
        phoneNumber: phoneNumber || undefined,
        validationErrors:
          Object.keys(updatedErrors).length > 0 ? updatedErrors : undefined,
      })
      setPhoneNumberError(error)
      isUpdatingRef.current = false
      return
    }

    setPhoneNumberError(error)

    updateNodeData(id, {
      ...dataRef.current,
      phoneNumber: phoneNumber || undefined,
    })
    isUpdatingRef.current = false
  }, [id, updateNodeData, phoneNumber])

  const handleSendFromChange = useCallback(
    (sendFrom: string | undefined) => {
      updateNodeData(id, {
        ...dataRef.current,
        sendFrom,
      })
    },
    [id, updateNodeData],
  )

  const handleAutoReplyChange = useCallback(
    (autoReplyToCaller: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        autoReplyToCaller,
      })
    },
    [id, updateNodeData],
  )

  const currentAutoReplyToCaller = data.autoReplyToCaller === true
  const currentMinimized = data.minimized ?? false

  const handleMinimizedChange = useCallback(
    (minimized: boolean) => {
      updateNodeData(id, {
        ...dataRef.current,
        minimized,
      })
    },
    [id, updateNodeData],
  )

  const hasData = Boolean(
    phoneNumber || message || data.sendFrom || data.autoReplyToCaller,
  )

  return {
    message,
    phoneNumber,
    handleMessageTypeChange,
    handleMessageChange,
    handleMessageBlur,
    handlePhoneNumberChange,
    handlePhoneNumberBlur,
    handleSendFromChange,
    handleAutoReplyChange,
    currentAutoReplyToCaller,
    currentMinimized,
    handleMinimizedChange,
    messageError,
    phoneNumberError,
    hasData,
  }
}
